import type { AnyVariables, DocumentInput } from "@urql/core"
import { createMutation } from "@urql/solid"
import { unwrap } from "solid-js/store"
import { type FragmentOf, graphql, readFragment } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { toast } from "@/lib/toast"

export const ErrorFragment = graphql(`
  fragment ErrorFragment on Error {
    __typename
    message
  }
`)

type MutationResult<TData> =
  | { success: true; data: TData }
  | { success: false; error: Error }

type MutationOptions<TData> = {
  onSuccess?: (data: TData) => void | Promise<void>
  onError?: (error: Error) => void | Promise<void>
  muteGraphQlUnionToast?: boolean
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

type ExtractDataField<T> = T extends { data: infer D } ? D : never

function hasDataField<T extends Record<string, unknown>, D>(
  value: T,
): value is T & { data: D } {
  return "data" in value && value.data !== undefined
}

type HasErrorShape<T> =
  T extends FragmentOf<typeof ErrorFragment> ? true : false

type UnionIncludesError<T> = T extends unknown ? HasErrorShape<T> : never

type AnyTrue<T> = T extends true ? true : never

type AssertHasErrorFragment<T, TKey> =
  AnyTrue<UnionIncludesError<T>> extends never
    ? never & {
        ERROR: "GraphQL mutation must include '...ErrorFragment' - the result type must be a union with ErrorFragment"
      }
    : TKey

export function createMutationWithErrorHandling<
  TData extends Record<string, unknown>,
  TVariables extends AnyVariables = AnyVariables,
  TKey extends keyof TData = keyof TData,
>(
  mutation: DocumentInput<TData, TVariables>,
  mutationKey: AssertHasErrorFragment<TData[TKey], TKey & string>,
) {
  type RawResult = TData[TKey]
  type SuccessResult = Exclude<RawResult, FragmentOf<typeof ErrorFragment>>
  type ResultData = ExtractDataField<SuccessResult>

  const { LL } = useI18nContext()
  const [state, executeMutation] = createMutation<TData, TVariables>(mutation)

  const mutate = async (
    variables: TVariables,
    options?: MutationOptions<ResultData>,
  ): Promise<MutationResult<ResultData>> => {
    const result = await executeMutation(variables)

    if (result.error) {
      console.error("Mutation error:", result.error)
      const isEmailNotVerified = result.error.graphQLErrors?.some(
        e => e.message === "EMAIL_NOT_VERIFIED",
      )
      const isNetworkError = result.error.networkError !== undefined
      toast.error(
        isEmailNotVerified
          ? LL().auth.emailNotVerified()
          : isNetworkError
            ? LL().common.networkError()
            : LL().common.error(),
      )
      await options?.onError?.(result.error)
      return { success: false, error: result.error }
    }

    const mutationResult = result.data?.[mutationKey]
    const payload =
      isRecord(mutationResult) && !Array.isArray(mutationResult)
        ? (unwrap(mutationResult as never) as Record<string, unknown>)
        : mutationResult

    if (!isRecord(payload)) {
      console.error("Invalid mutation result type")
      toast.error(LL().common.error())
      const error = new Error("Invalid mutation response")
      await options?.onError?.(error)
      return { success: false, error }
    }

    if (hasDataField<Record<string, unknown>, ResultData>(payload)) {
      await options?.onSuccess?.(payload.data)
      return { success: true, data: payload.data }
    }

    const errorData = readFragment(
      ErrorFragment,
      payload as unknown as FragmentOf<typeof ErrorFragment>,
    )

    if (errorData) {
      const errorMessage = errorData.message
      console.error("Mutation error:", errorMessage)

      if (!options?.muteGraphQlUnionToast) {
        const isValidationError = errorData.__typename === "ValidationError"
        const isFlagged = errorData.__typename === "FlaggedMessageError"
        toast.error(
          isValidationError
            ? LL().common.validationError()
            : isFlagged
              ? LL().comments.flaggedMessage()
              : LL().common.error(),
        )
      }
      const error = new Error(errorMessage)
      await options?.onError?.(error)
      return { success: false, error }
    }

    if (payload.message === "FLAGGED_MESSAGE") {
      const error = new Error("FLAGGED_MESSAGE")
      if (!options?.muteGraphQlUnionToast)
        toast.error(LL().comments.flaggedMessage())
      await options?.onError?.(error)
      return { success: false, error }
    }

    console.error("Unexpected mutation result type")
    toast.error(LL().common.error())
    const error = new Error("Unexpected mutation response")
    await options?.onError?.(error)
    return { success: false, error }
  }

  return [state, mutate] as const
}
